/**
 * La linea del saldo col fido (26/10/2026; CAPITOLATO: "il calendario c'è già: in testa la
 * linea del saldo col fido").
 *
 * La domanda: quando i soldi non bastano?
 * La forma: una linea sola, il saldo previsto a fine giornata da oggi in avanti (a gradini: il
 * saldo resta fermo fra un pagamento e l'altro). Lo zero e il fido Qonto (-5.000) sono due righe
 * sottili; sotto il fido la zona è rossa tenue e la linea diventa rossa. Etichette solo dove
 * servono (oggi e il punto più basso); il resto lo dice il riquadro che segue il mouse. Un clic
 * apre il giorno. La logica del fido è quella del calendario (admin.calendario.ts): qui si disegna.
 * Colori: solo token. Il rosso è l'unico colore, ed è l'allarme (oltre il fido).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { fmtEur } from '../../../lib/utils';
import type { CalendarioPagamenti } from './debitiApi';

const H = 176;
const M = { top: 18, right: 14, bottom: 24, left: 52 };
const MESI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const GIORNI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];

const tempo = (iso: string) => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
const giornoLungo = (iso: string) => {
  const t = new Date(tempo(iso));
  return `${GIORNI[t.getUTCDay()]} ${t.getUTCDate()} ${MESI[t.getUTCMonth()]}`;
};
/** "5k", "−5k", "0": le tacche dell'asse, corte. */
const kappa = (n: number) => (n === 0 ? '0' : `${n < 0 ? '−' : ''}${Math.abs(n) / 1000}k`);

/** Tacche "pulite" fra min e max: al massimo cinque. */
function tacche(min: number, max: number): number[] {
  const passi = [1000, 2000, 2500, 5000, 10000, 20000, 25000, 50000];
  const passo = passi.find((p) => (max - min) / p <= 5) ?? 100000;
  const out: number[] = [];
  for (let v = Math.ceil(min / passo) * passo; v <= max; v += passo) out.push(v);
  return out;
}

export function LineaSaldo({ d, onApri }: { d: CalendarioPagamenti; onApri: (data: string) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const fido = d.fido ?? 0;
  // I punti: oggi col saldo di oggi, poi ogni giorno con un saldo previsto.
  const punti = useMemo(() => {
    const p = d.giorni
      .filter((g) => g.saldo != null && g.data >= d.oggi)
      .map((g) => ({ data: g.data, saldo: g.saldo as number, voci: g.voci.length }));
    if (!p.length || p[0]!.data !== d.oggi) p.unshift({ data: d.oggi, saldo: d.saldoOggi, voci: 0 });
    return p;
  }, [d]);

  if (punti.length < 2) return null;
  const t0 = tempo(d.oggi);
  const t1 = Math.max(tempo(d.a), tempo(punti[punti.length - 1]!.data));
  const valori = punti.map((p) => p.saldo);
  const lo = Math.min(...valori, -fido, 0);
  const hi = Math.max(...valori, 0);
  const pad = (hi - lo) * 0.08 || 1000;
  const y0 = lo - pad;
  const y1 = hi + pad;
  const iw = Math.max(0, w - M.left - M.right);
  const ih = H - M.top - M.bottom;
  const x = (iso: string) => M.left + ((tempo(iso) - t0) / (t1 - t0 || 1)) * iw;
  const y = (v: number) => M.top + (1 - (v - y0) / (y1 - y0)) * ih;
  const xFine = M.left + iw;

  // A gradini: il saldo resta fermo fino al pagamento dopo.
  let path = `M ${x(punti[0]!.data)} ${y(punti[0]!.saldo)}`;
  for (let i = 1; i < punti.length; i++) path += ` H ${x(punti[i]!.data)} V ${y(punti[i]!.saldo)}`;
  path += ` H ${xFine}`;

  const yFido = y(-fido);
  const minimo = punti.reduce((m, p) => (p.saldo < m.saldo ? p : m), punti[0]!);
  const mesi: string[] = [];
  for (let t = new Date(t0); t.getTime() <= t1; t = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 1))) {
    const iso = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), 1)).toISOString().slice(0, 10);
    if (tempo(iso) >= t0) mesi.push(iso);
  }

  const vicino = (px: number) => {
    let best = 0;
    let dist = Infinity;
    punti.forEach((p, i) => { const dd = Math.abs(x(p.data) - px); if (dd < dist) { dist = dd; best = i; } });
    return best;
  };
  const h = hover != null ? punti[hover] : null;
  const tono = (v: number) => (v < -fido ? 'oltre il fido Qonto' : v < 0 ? 'sotto zero, coperto dal fido' : 'sopra zero');
  const descrizione = `Saldo previsto da oggi al ${giornoLungo(punti[punti.length - 1]!.data)}: il punto più basso è ${giornoLungo(minimo.data)}, € ${fmtEur(minimo.saldo)}, ${tono(minimo.saldo)}.`;

  return (
    <div className="cal-linea" ref={box}>
      {w > 0 && (
        <svg width={w} height={H} role="img" aria-label={descrizione}
          onPointerMove={(e) => { const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect(); setHover(vicino(e.clientX - r.left)); }}
          onPointerLeave={() => setHover(null)}
          onClick={() => { if (h && h.voci > 0) onApri(h.data); }}
          style={{ cursor: h && h.voci > 0 ? 'pointer' : 'default' }}>
          <defs>
            <clipPath id="cal-sopra-fido"><rect x={0} y={0} width={w} height={Math.max(0, yFido)} /></clipPath>
            <clipPath id="cal-sotto-fido"><rect x={0} y={yFido} width={w} height={Math.max(0, H - yFido)} /></clipPath>
          </defs>
          {/* La zona oltre il fido: rossa tenue. */}
          {fido > 0 && yFido < M.top + ih && <rect className="cal-linea-oltre" x={M.left} y={yFido} width={iw} height={M.top + ih - yFido} />}
          {tacche(y0, y1).map((v) => (
            <g key={v}>
              <line className={v === 0 ? 'cal-linea-zero' : 'cal-linea-griglia'} x1={M.left} x2={xFine} y1={y(v)} y2={y(v)} />
              <text className="cal-linea-asse" x={M.left - 8} y={y(v)} dy="0.32em" textAnchor="end">{kappa(v)}</text>
            </g>
          ))}
          {fido > 0 && (
            <>
              <line className="cal-linea-fido" x1={M.left} x2={xFine} y1={yFido} y2={yFido} />
              <text className="cal-linea-nota" x={xFine} y={yFido - 6} textAnchor="end">fido Qonto, fino a € −{fmtEur(fido)}</text>
            </>
          )}
          {mesi.map((m) => (
            <text key={m} className="cal-linea-asse" x={x(m)} y={H - 6} textAnchor="start">{MESI[Number(m.slice(5, 7)) - 1]}</text>
          ))}
          <path className="cal-linea-path" d={path} clipPath="url(#cal-sopra-fido)" />
          <path className="cal-linea-path is-oltre" d={path} clipPath="url(#cal-sotto-fido)" />
          {/* Le due etichette: oggi e il punto più basso. */}
          <circle className="cal-linea-punto" cx={x(punti[0]!.data)} cy={y(punti[0]!.saldo)} r={4} />
          {minimo.data !== punti[0]!.data && (
            <>
              <circle className={`cal-linea-punto${minimo.saldo < -fido ? ' is-oltre' : ''}`} cx={x(minimo.data)} cy={y(minimo.saldo)} r={4} />
              {/* Nella metà bassa l'etichetta sta sopra il punto, se no finirebbe sui mesi. */}
              <text className="cal-linea-etichetta" x={Math.min(x(minimo.data) + 8, xFine - 4)}
                y={y(minimo.saldo) > M.top + ih / 2 ? y(minimo.saldo) - 10 : y(minimo.saldo) + 16}
                textAnchor={x(minimo.data) > xFine - 160 ? 'end' : 'start'}>
                il punto più basso: € {fmtEur(minimo.saldo)}
              </text>
            </>
          )}
          {h && (
            <>
              <line className="cal-linea-mirino" x1={x(h.data)} x2={x(h.data)} y1={M.top} y2={M.top + ih} />
              <circle className={`cal-linea-punto${h.saldo < -fido ? ' is-oltre' : ''}`} cx={x(h.data)} cy={y(h.saldo)} r={5} />
            </>
          )}
        </svg>
      )}
      {h && w > 0 && (
        <div className="cal-linea-tip" style={{ left: Math.min(Math.max(x(h.data) + 12, 0), w - 220), top: 8 }}>
          <div className="t">{h.data === d.oggi ? 'Oggi' : giornoLungo(h.data)}</div>
          <div className={`v${h.saldo < -fido ? ' is-oltre' : ''}`}>€ {fmtEur(h.saldo, true)}</div>
          <div className="s">{tono(h.saldo)}{h.voci > 0 ? ' · clicca per aprire il giorno' : ''}</div>
        </div>
      )}
    </div>
  );
}
